// Third-party
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import { useTheme } from '@mui/material';
import { styled } from '@mui/system';

// Components
import { useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid2';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { TCustomTheme } from '@mgm/common/theme';
import Chart, { useChart, ApexOptions } from '@mgm/common/components/charts';

// Types

// Utils
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { formatNumber } from '../../../utils/helpers';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getDashboardData } from '../../../store/dashboard/selectors';
import { usePlatformData } from '../../../hooks/usePlatformData';
import { getCurrencyByCode } from '../../../store/common/selectors';
import { TDashboardOrders } from '../../../types';

const CHART_HEIGHT = 465;
const LEGEND_HEIGHT = 120;

const StyledChart = styled('div')(({ theme }) => ({
  height: CHART_HEIGHT,
  marginTop: theme.spacing(5),
  '& .apexcharts-canvas svg': {
    height: CHART_HEIGHT,
  },
  '& .apexcharts-canvas svg,.apexcharts-canvas foreignObject': {
    overflow: 'visible',
  },
  '& .apexcharts-legend': {
    height: LEGEND_HEIGHT,
    alignContent: 'center',
    borderTop: `solid 1px ${theme.palette.divider}`,
    top: `calc(${CHART_HEIGHT - LEGEND_HEIGHT}px) !important`,
  },
}));

const DashboardLocationSales = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { orders } = useAppSelector(getDashboardData);
  const chartRef = useRef<any>(null);
  const isChartMounted = useRef(false);

  const getFirstKey = (obj: TDashboardOrders['salesByLocationPieGraph']) => {
    const keys = Object.keys(obj);
    return keys?.[0] || [];
  };

  const theme = useTheme() as TCustomTheme;

  const [selectedOption, setSelectedOption] = useState(getFirstKey(orders?.salesByLocationPieGraph));
  const { locationsMapIdToName } = usePlatformData();
  const getCurrency = useAppSelector(getCurrencyByCode);

  useEffect(() => {
    setSelectedOption(getFirstKey(orders?.salesByLocationPieGraph));
  }, [orders]);

  const locationLabels: Array<string> = get(orders?.salesByLocationPieGraph, `${selectedOption}.labels`, []);

  useEffect(() => {
    if (isChartMounted.current && chartRef.current?.updateOptions) {
      try {
        chartRef.current?.updateOptions({
          labels: locationLabels?.map((location) => (location === 'unassigned' ? location : locationsMapIdToName?.[Number(location) || 0])),
          tooltip: {
            fillSeriesColor: false,
            y: {
              formatter: (seriesName: string) => `${formatNumber(seriesName)} ${getCurrency().mark}`,
              title: {
                formatter: (seriesName: string) => `${seriesName}`,
              },
            },
          },
          plotOptions: {
            pie: {
              donut: {
                size: '90%',
                labels: {
                  value: {
                    formatter: (val: string) => `${formatNumber(val)} ${getCurrency().mark}`,
                  },
                  total: {
                    formatter: (w: any) => {
                      const sum = w.globals.seriesTotals?.reduce((a: number, b: number) => a + b, 0);
                      return `${formatNumber(sum)} ${getCurrency().mark}`;
                    },
                  },
                },
              },
            },
          },
        });
      } catch (error) {
        console.error('Failed to update chart options:', error);
      }
    }
  }, [locationLabels]);

  const chartOptions = useChart({
    chart: {
      sparkline: {
        enabled: true,
      },
      events: {
        mounted: (chart: any) => {
          isChartMounted.current = true;
          chartRef.current = chart;
        },
      },
    },
    labels: locationLabels.map((location) => (location === 'unassigned' ? location : locationsMapIdToName?.[Number(location) || 0])),
    stroke: { colors: [theme.palette.background.paper] },
    legend: { floating: true, horizontalAlign: 'center' },
    tooltip: {
      fillSeriesColor: false,
      y: {
        formatter: (seriesName: string) => `${formatNumber(seriesName)} ${getCurrency().mark}`,
        title: {
          formatter: (seriesName: string) => `${seriesName}`,
        },
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '90%',
          labels: {
            value: {
              formatter: (val: string) => `${formatNumber(val)} ${getCurrency().mark}`,
            },
            total: {
              formatter: (w: any) => {
                const sum = w.globals.seriesTotals?.reduce((a: number, b: number) => a + b, 0);
                return `${formatNumber(sum)} ${getCurrency().mark}`;
              },
            },
          },
        },
      },
    },
  } as unknown as ApexOptions);

  return (
    <Card>
      <Grid container>
        <Grid size={{
          lg: 6,
          md: 6,
          xs: 12,
          sm: 6,
        }}
        >
          <CardHeader title={t('salesByLocation')} />
        </Grid>
      </Grid>
      <StyledChart dir="ltr">
        {!locationLabels.includes('No Data')
        && !locationLabels.includes('unknown')
        && !locationLabels.includes('N/A') && locationLabels?.length > 0 ? (
          <Chart
            type="donut"
            series={[...get(orders?.salesByLocationPieGraph, `${selectedOption}.values`, [])]}
            options={chartOptions}
            height={280}
          />
          ) : <CustomNoRowsOverlay text={t('looksLikeTheresNoDataForTheSelectedMonth', { ns: 'common' })} /> }
      </StyledChart>
    </Card>
  );
};

export {
  DashboardLocationSales,
};
